import { List, ListItem, ListItemText } from "@material-ui/core";
import { createStyles, makeStyles } from "@material-ui/styles";
import {
    CandidateIcon,
    CircleIcon,
    CopyIcon,
    Divider,
    EditIcon,
    FilterIcon,
    ISearchFacetConfig,
    JobStatusLabel,
    Link,
    LocationOption,
    mediator,
    MessageComposerIconButton,
    MobilePage,
    MobilePageActions,
    MobilePageSection,
    PageActionButton,
    PaginatorMobile,
    RelodeTheme,
    SearchFiltersMobile,
    SortMobile,
    SortOption,
    Text,
    useRouter,
} from "@relode/core";
import SortIcon from "@relode/core/assets/icons/sortIcon";
import { JobCandidateStatus } from "@relode/core/domain";
import qs from "qs";
import * as React from "react";

import { jobs } from "../../../shared/backend/controllers";
import { useJobSearch } from "../_infra/search/jobSearchContext";

const useStyles = makeStyles(
    (theme: RelodeTheme) =>
        createStyles({
            keywordInput: {
                margin: theme.spacing(1, 0, 2, 0),
            },
            searchButton: {
                padding: 0,
            },
            searchButtonIcon: {
                fontSize: "1rem",
            },
            listItemContent: {
                paddingLeft: theme.spacing(2),
                paddingRight: theme.spacing(2),
            },
            sortAndFilterContainer: {
                position: "absolute",
                right: 0,
                marginRight: theme.spacing(2),
            },
            sortLink: { marginRight: theme.spacing(3) },
            sortIcon: {
                paddingTop: "2px",
            },
            circle: {
                fontSize: "0.5em",
                color: theme.palette.action.active,
                marginTop: theme.spacing(1),
                marginRight: theme.spacing(1),
                marginLeft: theme.spacing(1),
            },
            jobActions: {
                display: "flex",
                justifyContent: "space-evenly",
                width: "100%",
            },
            jobListItemData: {
                backgroundColor: theme.palette.common.white,
            },
            jobListItemActions: {
                backgroundColor: theme.palette.common.white,
                padding: 0,
            },
            jobStatus: {
                marginRight: 0,
                marginTop: theme.spacing(0.5),
            },
            candidateCount: {
                color: theme.palette.primary.main,
            },
            noCandidates: {
                color: theme.palette.primary.main,
                opacity: 0.3,
            },
            statusSection: {
                display: "flex",
                margin: theme.spacing(1, 0),
            },
            noResultsText: {
                margin: theme.spacing(5),
            },
        }),
    { name: "JobSearchMobile" }
);

interface InternalSearchState {
    searchText?: string;
    distanceRadius?: number;
    selectedLocationId?: number;
    selectedLocationName?: string;
}

export const JobSearchMobile = () => {
    const classes = useStyles({});
    const [searchState, searchActions] = useJobSearch();
    const { history, location } = useRouter();

    const [internalSearchState, setInternalSearchState] =
        React.useState<InternalSearchState>({});

    const [backUrl, setBackUrl] = React.useState<string>(undefined);

    const [isFiltersSectionOpen, setIsFiltersSectionOpen] =
        React.useState<boolean>(false);
    const [isSortOpen, setIsSortOpen] = React.useState<boolean>(false);

    const { searchResults, searchCriteria } = searchState;

    React.useEffect(() => {
        if (!!location.search) {
            setBackUrl(`/jobs${location.search}`);
        } else {
            setBackUrl("/jobs");
        }
    }, [location.search]);

    React.useEffect(() => {
        const initInternalSearchState: InternalSearchState = {
            searchText: searchCriteria.searchText || "",
            distanceRadius:
                searchCriteria.searchRadiusInMeters >= 0
                    ? searchCriteria.searchRadiusInMeters
                    : relodeConstants.DEFAULT_SEARCH_RADIUS_METERS,
            selectedLocationId: searchCriteria.locationId,
            selectedLocationName: searchCriteria.locationName,
        };
        setInternalSearchState(initInternalSearchState);
    }, [searchCriteria]);

    const handleSearch = (searchText: string) => {
        setInternalSearchState({
            ...internalSearchState,
            searchText: searchText,
        });
        searchActions.searchBySelectedFilters(
            searchText,
            internalSearchState.distanceRadius,
            internalSearchState.selectedLocationId,
            internalSearchState.selectedLocationName
        );
        window.scrollTo(0, 0);
    };

    const handleLocationFilter = (option: LocationOption) => {
        setInternalSearchState({
            ...internalSearchState,
            selectedLocationId: option?.locationId,
            selectedLocationName: option?.locationName,
        });
    };

    const handleDistanceFilter = (miles: number) => {
        setInternalSearchState({
            ...internalSearchState,
            distanceRadius: miles,
        });
    };

    const handleMultipleSelectionFilter =
        (fId: string, fName: string, itemName?: string) =>
        (catItem?: { label: string; value: string }) => {
            searchActions.filterMultiple(fId, fName, itemName, catItem);
        };

    const handleFilter =
        (fId: string, fName: string) =>
        (catItem?: { label: string; value: string }) => {
            searchActions.filterSingle(fId, fName, catItem);
        };

    const handleJobSearchRefresh = async () => {
        await searchActions.search(searchCriteria);
    };

    const handleCopyJob = (jobId: number) => {
        mediator.send("showAddJob", { jobId: jobId, isEditJob: false });
    };

    const handleEditJob = (jobId: number) => {
        mediator.send("showAddJob", {
            jobId: jobId,
            isEditJob: true,
            onComplete: handleJobSearchRefresh,
        });
    };

    const renderPagination = (): React.ReactNode => {
        const { pageSize, pageIndex } = searchCriteria;

        const start = (pageIndex - 1) * pageSize + 1;
        const remaining =
            searchResults?.totalResults < pageSize
                ? searchResults?.totalResults
                : searchResults?.totalResults - start;
        const maxOnPage =
            remaining < pageSize
                ? searchResults?.totalResults
                : pageIndex * pageSize;

        if (!!searchResults && searchResults?.totalResults > 0) {
            return (
                <PaginatorMobile
                    baseId="searchJobs"
                    total={searchResults?.totalResults}
                    range={{
                        start,
                        end: maxOnPage,
                    }}
                    showAsRange
                    navigate={changePage}
                />
            );
        } else {
            return null;
        }
    };

    const goToDetails = (job: jobs.JobSearchJobResponse) => {
        const queryString = qs.stringify(searchCriteria, {
            skipNulls: true,
            sort: (a, b) => a.localeCompare(b),
        });

        history.push({
            pathname: `/jobs/${job.jobId}`,
            state: {
                ...location.state,
                queryString,
                backUrl,
            },
        });
    };

    const goToCandidatesSearch = (job: jobs.JobSearchJobResponse) => {
        const criteria = {
            jobId: job.jobId,
            jobTitleName: job.jobName.trim(),
            jobCandidateStatusIds: [
                JobCandidateStatus.Offered.id,
                JobCandidateStatus.Submitted.id,
                JobCandidateStatus.Interviewing.id,
                JobCandidateStatus.Hired.id,
            ],
            jobCandidateStatusNames: [
                JobCandidateStatus.Offered.description,
                JobCandidateStatus.Submitted.description,
                JobCandidateStatus.Interviewing.description,
                JobCandidateStatus.Hired.description,
            ],
            pageIndex: 1,
            pageSize: 25,
        };
        history.push(
            `/candidates${qs.stringify(criteria, {
                skipNulls: true,
                addQueryPrefix: true,
                sort: (a, b) => a.localeCompare(b),
            })}`
        );
    };

    const facetConfig: ISearchFacetConfig = {
        handleMultipleSelection: handleMultipleSelectionFilter,
        handleSingleSelection: handleFilter,
        facets: [
            {
                id: "jobStatus",
                title: "job status",
                idPropName: "jobStatusIds",
                namePropName: "jobStatusNames",
                isMultiple: true,
            },
            {
                id: "location",
                title: "job location",
                idPropName: "locationId",
                namePropName: "locationName",
                isMultiple: false,
            },
            {
                id: "jobPriorityType",
                title: "job grade",
                idPropName: "jobPriorityTypeId",
                namePropName: "jobPriorityTypeName",
                isMultiple: false,
            },
            {
                id: "jobType",
                title: "job type",
                idPropName: "jobTypeIds",
                namePropName: "jobTypeNames",
                isMultiple: true,
            },
            {
                id: "team",
                title: "organization",
                idPropName: "teamId",
                namePropName: "TeamName",
                isMultiple: false,
            },
            {
                id: "jobScope",
                title: "job scope",
                idPropName: "jobScope",
                namePropName: "jobScopeName",
                isMultiple: false,
            },
        ],
    };

    const getSortOptions = (): SortOption[] => [
        {
            field: "jobName",
            title: "Job: A to Z",
            direction: "asc",
        },
        {
            field: "jobName",
            title: "Job: Z to A",
            direction: "desc",
        },
        {
            field: "daysOpen",
            title: "Days Open: High to Low",
            direction: "desc",
        },
        {
            field: "daysOpen",
            title: "Days Open: Low to High",
            direction: "asc",
        },
        {
            field: "candidateFunnelCount",
            title: "Candidate Pipeline: High to Low",
            direction: "desc",
        },
        {
            field: "candidateFunnelCount",
            title: "Candidate Pipeline: Low to High",
            direction: "asc",
        },
    ];

    const handleRequestSort = (field: string, direction: SortDir) => {
        searchActions.sortMobile(field, direction);
        window.scrollTo(0, 0);
    };

    const changePage = (direction: "prev" | "next") => {
        searchActions.navigate(direction);
        window.scrollTo(0, 0);
    };

    return (
        <>
            <MobilePage
                pageTitle="jobs"
                defaultSection="search"
                showTabs={false}
                busyType="bar"
                isBusy={searchState.searching}
                actions={
                    <MobilePageActions title="jobs">
                        <div className={classes.sortAndFilterContainer}>
                            <Link
                                className={classes.sortLink}
                                onClick={() => setIsSortOpen(true)}
                            >
                                <Text variant="body1" color="primary">
                                    <SortIcon className={classes.sortIcon} />{" "}
                                    sort
                                </Text>
                            </Link>
                            <Link onClick={() => setIsFiltersSectionOpen(true)}>
                                <Text variant="body1" color="primary">
                                    <FilterIcon fontSize="small" /> filters
                                </Text>
                            </Link>
                        </div>
                    </MobilePageActions>
                }
            >
                <MobilePageSection id="search">
                    <div>
                        {(searchResults?.jobs?.length ?? 0) > 0 ? (
                            <List id="jobListing" component="div">
                                {searchResults.jobs.map((job, i) => (
                                    <JobListItem
                                        key={i}
                                        job={job}
                                        onClick={goToDetails}
                                        handleCandidatesClick={() =>
                                            goToCandidatesSearch(job)
                                        }
                                        handleCopyJob={
                                            searchResults.canCopy
                                                ? () => handleCopyJob(job.jobId)
                                                : null
                                        }
                                        handleEditJob={
                                            job.canEdit
                                                ? () => handleEditJob(job.jobId)
                                                : null
                                        }
                                    />
                                ))}
                            </List>
                        ) : (
                            <div
                                data-cy="noResultsText"
                                className={classes.noResultsText}
                            >
                                <Text variant="h4" color="primary">
                                    Sorry, no jobs found.
                                </Text>
                            </div>
                        )}
                    </div>
                    {renderPagination()}
                </MobilePageSection>
            </MobilePage>
            <SortMobile
                isOpen={isSortOpen}
                handleIsOpen={(val: boolean) => setIsSortOpen(val)}
                handleRequestSort={handleRequestSort}
                searchCriteria={searchState.searchCriteria}
                sortOptions={getSortOptions()}
            />
            <SearchFiltersMobile
                isOpen={isFiltersSectionOpen}
                onOpenChanged={(val: boolean) => setIsFiltersSectionOpen(val)}
                searchCriteria={searchState.searchCriteria}
                onSearch={handleSearch}
                facets={searchResults?.facets}
                facetConfig={facetConfig}
                hasResults={searchResults?.totalResults > 0}
                onLocationFilter={handleLocationFilter}
                selectedLocationId={internalSearchState.selectedLocationId}
                selectedLocationName={internalSearchState.selectedLocationName}
                distanceRadius={internalSearchState.distanceRadius}
                onDistanceFilter={handleDistanceFilter}
            />
        </>
    );
};

const JobListItem = (props: {
    job: jobs.JobSearchJobResponse;
    onClick: (job: jobs.JobSearchJobResponse) => void;
    handleCandidatesClick: (job: jobs.JobSearchJobResponse) => void;
    handleCopyJob?: (jobId: number) => void;
    handleEditJob?: (jobId: number) => void;
}) => {
    const {
        job,
        onClick,
        handleCandidatesClick,
        handleCopyJob,
        handleEditJob,
    } = props;
    const classes = useStyles({});

    const handleClick = () => {
        onClick(job);
    };

    return (
        <>
            <ListItem
                button
                disableGutters
                alignItems="flex-start"
                onClick={handleClick}
                className={classes.jobListItemData}
            >
                <ListItemText
                    primary={job.jobName}
                    primaryTypographyProps={{
                        variant: "h5",
                        color: "primary",
                    }}
                    secondaryTypographyProps={{ component: "div" }}
                    secondary={
                        <>
                            <div className={classes.statusSection}>
                                <JobStatusLabel
                                    className={classes.jobStatus}
                                    jobStatus={job.jobStatusId}
                                    title={job.jobName}
                                />
                                <CircleIcon className={classes.circle} />
                                <Text component="span">{`${job.jobTypeName} `}</Text>
                                {job.daysOpen !== null &&
                                job.daysOpen !== undefined ? (
                                    <>
                                        <CircleIcon
                                            className={classes.circle}
                                        />
                                        <Text component="span">{` ${job.daysOpen} days open`}</Text>
                                    </>
                                ) : null}
                            </div>
                            <div>
                                <Text component="span">{job.teamName}</Text>
                                <CircleIcon className={classes.circle} />
                                <Text component="span">{` ${job.locationName}`}</Text>
                            </div>
                        </>
                    }
                    className={classes.listItemContent}
                />
            </ListItem>
            <ListItem
                component="div"
                disableGutters
                alignItems="flex-start"
                className={classes.jobListItemActions}
            >
                <div className={classes.jobActions}>
                    <MessageComposerIconButton
                        jobId={job.jobId}
                        jobName={job.jobName}
                    />
                    <div>
                        <PageActionButton
                            onClick={() => handleCandidatesClick(job)}
                            id="candidates"
                            color="primary"
                            title="candidates"
                            className={
                                job.candidateFunnelCount > 0
                                    ? classes.candidateCount
                                    : classes.noCandidates
                            }
                        >
                            <CandidateIcon />
                        </PageActionButton>
                        <span
                            className={
                                job.candidateFunnelCount > 0
                                    ? classes.candidateCount
                                    : classes.noCandidates
                            }
                        >
                            {job.candidateFunnelCount}
                        </span>
                    </div>
                    {!!handleCopyJob ? (
                        <PageActionButton
                            onClick={() => handleCopyJob(job.jobId)}
                            id="copyJob"
                            color="primary"
                            title="copy job"
                        >
                            <CopyIcon />
                        </PageActionButton>
                    ) : null}
                    {!!handleEditJob ? (
                        <PageActionButton
                            onClick={() => handleEditJob(job.jobId)}
                            id="editJob"
                            color="primary"
                            title="edit job"
                        >
                            <EditIcon />
                        </PageActionButton>
                    ) : null}
                </div>
            </ListItem>
            <Divider></Divider>
        </>
    );
};
